import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthChange,
  signIn as authSignIn,
  signOut as authSignOut,
  signUp as authSignUp,
  signInWithMagicLink as authSignInWithMagicLink,
  signInWithOAuth as authSignInWithOAuth,
  type OAuthProvider,
} from "@/auth";
import { createApiClient, type ApiClient } from "@/lib/api";
import { sentrySetUser, sentryClearUser } from "@/lib/sentry";
import {
  deriveAuthState,
  sentryIdentityFor,
  type AuthState,
  type SettledAuthState,
} from "@/lib/auth-state";

export type { AuthState };

export type AuthContextValue = {
  state: AuthState;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: string | null }>;
  signInWithOAuth: (provider: OAuthProvider) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Applies a derived state. The decision itself is `sentryIdentityFor` in lib/auth-state.ts —
 * only the opaque user id ever reaches Sentry, never the account email.
 */
function syncSentryIdentity(next: SettledAuthState): void {
  const identity = sentryIdentityFor(next);
  if (identity.kind === "set") sentrySetUser({ id: identity.id });
  else if (identity.kind === "clear") sentryClearUser();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  // All that is left here is the subscription. Everything it decides lives in lib/auth-state.ts,
  // because a decision inside this callback can only be reached by rendering a React tree.
  useEffect(() => {
    const unsubscribe = onAuthChange((token, email, userId) => {
      const next = deriveAuthState(token, email, userId, (t) => createApiClient(t) as ApiClient);
      setState(next);
      syncSentryIdentity(next);
    });
    return unsubscribe;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    return authSignIn(email, password);
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    return authSignUp(email, password);
  }, []);

  const signInWithMagicLink = useCallback(async (email: string) => {
    return authSignInWithMagicLink(email);
  }, []);

  const signInWithOAuth = useCallback(async (provider: OAuthProvider) => {
    return authSignInWithOAuth(provider);
  }, []);

  const signOut = useCallback(async () => {
    await authSignOut();
    // Through the same derivation: signing out is "no token", and it must not be a second,
    // hand-written definition of what being signed out means.
    const next = deriveAuthState(null, null, null, (t) => createApiClient(t) as ApiClient);
    setState(next);
    syncSentryIdentity(next);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      signIn,
      signUp,
      signInWithMagicLink,
      signInWithOAuth,
      signOut,
    }),
    [state, signIn, signUp, signInWithMagicLink, signInWithOAuth, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
